import { Node } from '@tiptap/core'

const PROVIDERS = {
  youtube: {
    patterns: [/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/, /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/],
    embedSrc: (id) => `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`,
    title: 'YouTube video player',
  },
  vimeo: {
    patterns: [/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/],
    // Legacy v1 packed an unlisted video's privacy hash into the id as "videoId/hash" (the
    // backend only preserves the `id` attribute). Unpack it back into the `?h=` query.
    embedSrc: (id) => {
      const [videoId, hash] = String(id).split('/')
      return `https://player.vimeo.com/video/${videoId}${hash ? `?h=${hash}` : ''}`
    },
    title: 'Vimeo video player',
  },
}

export function detectVideo(url) {
  if (!url) return null
  for (const [provider, config] of Object.entries(PROVIDERS)) {
    for (const pattern of config.patterns) {
      const match = url.match(pattern)
      if (match?.[1]) return { provider, id: match[1] }
    }
  }
  return null
}

export const Video = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      provider: { default: null },
      videoId: { default: null },
    }
  },

  // Recognize the legacy bare tags so the SAME node powers editing, the v1 view,
  // and migration (generateJSON over Quill HTML).
  parseHTML() {
    return Object.keys(PROVIDERS).map((provider) => ({
      tag: provider,
      getAttrs: (node) => ({ provider, videoId: node.getAttribute('id') }),
    }))
  },

  // Serialize back to a bare <provider id="..."> tag (used for copy/paste and any
  // HTML export); v2 storage itself is JSON via getJSON().
  renderHTML({ HTMLAttributes }) {
    const provider = PROVIDERS[HTMLAttributes.provider] ? HTMLAttributes.provider : 'youtube'
    return [provider, { id: HTMLAttributes.videoId }]
  },

  addNodeView() {
    return ({ node }) => {
      const config = PROVIDERS[node.attrs.provider]
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position:relative;padding-bottom:56.25%;height:0;margin:1em 0;background:#000;'
      if (config && node.attrs.videoId) {
        const iframe = document.createElement('iframe')
        iframe.src = config.embedSrc(node.attrs.videoId)
        iframe.title = config.title
        iframe.setAttribute('frameborder', '0')
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share')
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin')
        iframe.setAttribute('allowfullscreen', 'true')
        iframe.setAttribute('loading', 'lazy')
        iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border:none;'
        wrapper.appendChild(iframe)
      }
      return { dom: wrapper }
    }
  },

  addCommands() {
    return {
      insertVideo:
        ({ provider, videoId }) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { provider, videoId } }),
    }
  },
})
